import * as React from "react";
import { Card } from "@/components/ui/card";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  School,
  GraduationCap,
  Clock,
  Building,
  Activity,
  Download,
} from "lucide-react";

import { useGetStudentbyUsernameQuery } from "../store/studentApi";
import { useUpdateRankMutation } from "../store/studentApi";
import { useGetSingleFeeQuery } from "../store/feeApi";
import { useSelector } from "react-redux";
import { RootState } from "@/types";
import { Link, useParams } from "react-router-dom";
import { exportStudentProfile } from "@/hooks/useExportProfile";
import PortfolioComponent from "./PortfolioComponent";
import { useGetRankingQuery } from "@/store/ranking/rankingApi";
import AnimatedError404 from "./Error404";

const StudentProfile = () => {
  const [activeTab, setActiveTab] = React.useState("details");
  const { user } = useSelector((state: RootState) => state.user);
  const { username } = useParams();

  const { data: student } = useGetStudentbyUsernameQuery({
    Token: user?.token,
    username: username,
  });

  const [showModal, setShowModal] = React.useState(false);
  const [rank, setRank] = React.useState("");

  const { data: ranking } = useGetRankingQuery({
    studentId: student?._id || user?._id,
    batchCode: user?.batch?._id || student?.batch,
  });


  const getRankGrade = (score) => {
    if (!score) return "-";
    if (score >= 90) return "S";
    if (score >= 80) return "A";
    if (score >= 70) return "B";
    if (score >= 60) return "C";
    if (score >= 50) return "D";
    return "F";
  };

  const rankGrade = getRankGrade(ranking?.data?.ranking?.totalScore);
  const toggleModal = () => setShowModal(!showModal);

  const [UpdateRank, { isSuccess }] = useUpdateRankMutation();

  const handleSave = () => {
    const rankData = {
      id: student?._id,
      Token: user?.token,
      rank: rank,
    };
    UpdateRank(rankData);
  };

  React.useEffect(() => {
    if (isSuccess) {
      toggleModal();
    }
  }, [isSuccess]);
  const { data: fee } = useGetSingleFeeQuery(student?._id);

  if (student === undefined || student === null) {
    return <AnimatedError404 />
  }

  return (
    <div className="max-w-full mx-auto bg-background min-h-screen">
      <div className="p-4">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="w-20 h-20 sm:w-32 sm:h-32 bg-[#26BDB0] rounded-full flex items-center justify-center shrink-0 mx-auto sm:mx-0">
            <div className="w-20 text-primary h-20 sm:w-28 sm:h-28 overflow-hidden bg-[#26BDB0] rounded-full flex items-center justify-center text-2xl sm:text-4xl font-bold">
              {!student?.profile ? (
                <img
                  className="size-full object-cover "
                  src={student?.profile}
                  alt="Profile"
                />
              ) : (
                <h1 className="text-white">
                  {student?.fname.charAt(0)}
                  {student?.lname.charAt(0)}
                </h1>
              )}
            </div>
          </div>

          <div className="flex-1 w-full">
            <div className="flex flex-col justify-between sm:flex-row items-start sm:items-center gap-4 mb-4">
              <h2 className="text-xl text-foreground font-bold">
                {student?.fname.charAt(0).toUpperCase() +
                  student?.fname.slice(1).toLowerCase()}{" "}
                {student?.lname.charAt(0).toUpperCase() +
                  student?.lname.slice(1).toLowerCase()}
              </h2>
              <div className="flex gap-5 w-full sm:w-auto">
                {user?.role === "hr" && (
                  <button
                    onClick={toggleModal}
                    className="flex-1 sm:flex-none bg-black text-white px-4 py-2 rounded-lg hover:bg-black/80 transition-colors"
                  >
                    Rank
                  </button>
                )}

                <Link
                  to={`/feehistory/${student?._id}`}
                  className="flex-1 sm:flex-none bg-[#26BDB0] text-white px-4 py-2 rounded-lg hover:bg-[#26BDB0] transition-colors"
                >
                  View Fee History
                </Link>
                <button
                  onClick={() =>
                    student && fee && exportStudentProfile(student, fee)
                  }
                  className="flex-1 sm:flex-none bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors flex items-center gap-2"
                >
                  <Download size={16} /> Download
                </button>
              </div>
              {/* Modal */}
              {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-80">
                    <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
                      Rank
                    </h2>

                    <label className="block mb-2 text-gray-700 dark:text-gray-200">
                      Select Rank:
                    </label>
                    <select
                      value={rank}
                      onChange={(e) => setRank(e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">-- Choose --</option>
                      <option value="A+">A+</option>
                      <option value="A">A</option>
                      <option value="B+">B+</option>
                      <option value="B">B</option>
                      <option value="C+">C+</option>
                      <option value="C">C</option>
                      <option value="D+">D+</option>
                      <option value="D">D</option>
                    </select>

                    <div className="flex gap-3 mt-4">
                      <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-[#49ccc1e2] text-white rounded hover:bg-[#26BDB0]"
                      >
                        Save
                      </button>
                      <button
                        onClick={toggleModal}
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div
                role="region"
                aria-labelledby="total-fee-title"
                className="text-center p-3 bg-muted rounded-lg"
              >
                <div
                  id="total-fee-title"
                  className="text-lg font-bold text-primary"
                >
                  ₹{fee?.TotalFee}
                </div>
                <div className="text-sm text-muted-foreground">Total Fee</div>
              </div>
              <div
                role="region"
                aria-labelledby="paid-fee-title"
                className="text-center p-3 bg-muted rounded-lg"
              >
                <div
                  id="paid-fee-title"
                  className="text-lg text-primary font-bold text-success"
                >
                  ₹{fee?.PaidFee}
                </div>
                <div className="text-sm text-muted-foreground">Paid Fee</div>
              </div>
              <div
                role="region"
                aria-labelledby="pending-fee-title"
                className="text-center p-3 bg-muted rounded-lg"
              >
                <div
                  id="pending-fee-title"
                  className="text-lg font-bold text-destructive"
                >
                  ₹{fee?.TotalFee - fee?.PaidFee}
                </div>
                <div className="text-sm text-muted-foreground">Pending</div>
              </div>
            </div>

            <div className="text-sm">
              <div className="font-medium text-primary">
                Course: {student?.course}
              </div>
              <div className="text-muted-foreground">
                Batch: {student?.batch}
              </div>
              <div className="text-muted-foreground">Rank: {rankGrade}</div>
              <div className="text-muted-foreground">
                {<PortfolioComponent studentId={student?._id} />}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t">
        <div className="flex">
          {["Details", "Education", "Course"].map((tab) => (
            <button
              key={tab}
              className={`flex-1 py-3 text-sm font-medium ${activeTab === tab.toLowerCase()
                ? "border-t-2 border-[#26BDB0] text-[#26BDB0]"
                : "text-gray-500"
                }`}
              onClick={() => setActiveTab(tab.toLowerCase())}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4">
        {activeTab === "details" && (
          <div className="space-y-6">
            <DetailCard title="Personal Information">
              <DetailItem
                icon={User}
                label="Father Name"
                value={student?.father}
              />
              <DetailItem
                icon={Phone}
                label="Contact"
                value={student?.contact}
              />
              <DetailItem icon={Mail} label="Email" value={student?.email} />
              <DetailItem
                icon={User}
                label="Gender"
                value={student?.gender?.toUpperCase()}
              />
              <DetailItem
                icon={Calendar}
                label="Date of Birth"
                value={new Date(student?.dob).toLocaleDateString()}
              />
              <DetailItem
                icon={MapPin}
                label="Current Address"
                value={student?.curAddress}
              />
              <DetailItem
                icon={MapPin}
                label="Permanent Address"
                value={student?.perAddress}
              />
            </DetailCard>
          </div>
        )}

        {activeTab === "education" && (
          <div className="space-y-6">
            <DetailCard title="Education Details">
              <DetailItem
                icon={School}
                label="Degree"
                value={student?.degree}
              />
              <DetailItem
                icon={Calendar}
                label="Year of Completion"
                value={student?.comYear}
              />
              <DetailItem
                icon={Building}
                label="University/College"
                value={student?.university}
              />
            </DetailCard>
          </div>
        )}

        {activeTab === "course" && (
          <div className="space-y-6">
            <DetailCard title="Course Information">
              <DetailItem
                icon={Clock}
                label="Course Duration"
                value={student?.cduration}
              />
              <DetailItem
                icon={Calendar}
                label="Joining Date"
                value={new Date(student?.joiningDate).toLocaleDateString()}
              />
              <DetailItem
                icon={Calendar}
                label="End Date"
                value={student?.endDate || "Not mentioned yet"}
              />
              <DetailItem
                icon={Activity}
                label="Course Status"
                value={
                  student?.isCompleted === "No" ? "In Progress" : "Completed"
                }
              />
              <DetailItem
                icon={GraduationCap}
                label="Placement Status"
                value={student?.isPlaced == "yes" ? "Placed" : "Not Placed"}
              />
            </DetailCard>

            {user?.role !== "student" && (
              <button className="w-full bg-[#26BDB0] text-white py-3 rounded-lg hover:bg-[#49ccc1e2] transition-colors">
                <Link to={`/attendancehistory/${student?._id}`}>
                  View Attendance History
                </Link>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const DetailCard = ({ title, children }) => (
  <Card>
    <div className="p-4">
      <h3 className="font-medium mb-4">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  </Card>
);

const DetailItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="mt-1">
      <Icon size={18} className="text-gray-500" />
    </div>
    <div>
      <div className="text-sm text-gray-600">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  </div>
);

export default StudentProfile;
